import React, { useState, useEffect } from "react";
import { Loader2, ChevronDown, ChevronUp, QrCode } from "lucide-react";
import { Button } from "../ui/button";
import { api } from "../../lib/api";
import AdminTableToolbar from "./AdminTableToolbar";
import useAdminInfiniteList from "../../hooks/useAdminInfiniteList";
import { exportRowsToCSV } from "../../lib/csvExport";
import PromoterQRDialog from "./PromoterQRDialog";

/**
 * Promoters tab.
 *
 * Each row is a user who has at least one referral signup. Clicking
 * the chevron expands an inner table of all the people THAT promoter
 * brought (with their own status + donation summary).
 *
 * The referrals drill-down is lazy: we don't fetch it until the
 * promoter is expanded, and we cache the result so re-opening the
 * same row is instant.
 */

function StatusDot({ status }) {
  const color =
    status === "green"
      ? "bg-emerald-500"
      : status === "yellow"
      ? "bg-amber-500"
      : "bg-rose-500";
  return <span className={`inline-block w-2 h-2 rounded-full ${color}`} />;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("he-IL", {
      day: "numeric", month: "short", year: "2-digit",
    });
  } catch {
    return "—";
  }
}

function ReferralsPanel({ userId, includeDummy }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Lazy single-shot fetch on mount of this component (parent only
  // mounts us when the row is expanded — so this fires once per
  // expansion event).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .adminPromoterReferrals(userId, { include_dummy: includeDummy, limit: 200 })
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "טעינה נכשלה");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [userId, includeDummy]);

  if (loading) {
    return (
      <div className="py-4 flex items-center justify-center text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  if (error) {
    return <div className="py-3 text-xs text-destructive">{error}</div>;
  }
  if (!data || data.items.length === 0) {
    return <div className="py-3 text-xs text-muted-foreground italic">אין הפניות.</div>;
  }
  return (
    <div className="bg-card/20 rounded-md p-3" data-testid={`referrals-of-${userId}`}>
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">
        {data.items.length} משתמשים שהובאו ע"י מפיץ זה
      </p>
      <table className="w-full text-xs">
        <thead className="text-muted-foreground">
          <tr>
            <th className="text-right p-2">משתמש</th>
            <th className="text-right p-2">הצטרף</th>
            <th className="text-right p-2">סטטוס</th>
            <th className="text-right p-2">תרם</th>
          </tr>
        </thead>
        <tbody>
          {data.items.map((r) => (
            <tr key={r.id} className="border-t border-border/20">
              <td className="p-2">
                <div className="text-foreground">{r.name || "—"}</div>
                <div className="text-muted-foreground" dir="ltr">{r.email}</div>
              </td>
              <td className="p-2 text-muted-foreground">{formatDate(r.signup_date)}</td>
              <td className="p-2"><StatusDot status={r.status} /></td>
              <td className="p-2">
                {r.donated ? (
                  <span className="text-gold">
                    {(r.donation_currency || "ILS").toUpperCase() === "USD" ? "$" : "₪"}
                    {r.donation_amount}
                  </span>
                ) : (
                  <span className="text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AdminPromotersTab() {
  const [q, setQ] = useState("");
  const [includeDummy, setIncludeDummy] = useState(false);
  const [expanded, setExpanded] = useState(null); // user_id
  const [qrPromoter, setQrPromoter] = useState(null);

  const { items, total, loading, hasMore, sentinelRef } = useAdminInfiniteList({
    fetchPage: api.adminPromoters,
    params: { include_dummy: includeDummy },
  });

  const filtered = q
    ? items.filter((p) => {
        const hay = `${p.name || ""} ${p.email || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      })
    : items;

  const handleExport = () => {
    exportRowsToCSV({
      filename: `torahya-promoters-${new Date().toISOString().slice(0, 10)}.csv`,
      columns: [
        { key: "name", label: "שם" },
        { key: "email", label: "אימייל" },
        { key: "referral_code", label: "קוד הפניה" },
        { key: "signups_brought", label: "סה\"כ הצטרפויות" },
        { key: "signups_brought_this_week", label: "השבוע" },
        { key: "clicks_count", label: "לחיצות" },
        {
          key: "conversion_rate",
          label: "המרה %",
          fmt: (r) =>
            r.conversion_rate !== null && r.conversion_rate !== undefined
              ? `${(r.conversion_rate * 100).toFixed(1)}%`
              : "—",
        },
        { key: "referred_users_active", label: "פעילים" },
        { key: "referred_users_donated_count", label: "תרמו" },
        { key: "referred_users_donated_total", label: "סה\"כ תרומות" },
      ],
      rows: filtered,
    });
  };

  return (
    <div className="space-y-4" data-testid="admin-promoters">
      <AdminTableToolbar
        q={q}
        onSearch={setQ}
        searchPlaceholder="חיפוש מפיץ לפי שם או אימייל…"
        includeDummy={includeDummy}
        onToggleDummy={setIncludeDummy}
        onExportCSV={handleExport}
        exportDisabled={filtered.length === 0}
        testIdPrefix="admin-promoters"
      />

      <div className="text-xs text-muted-foreground">
        מציג {filtered.length} מפיצים
      </div>

      {/* Desktop: rows */}
      <div className="hidden md:block rounded-md border border-border/60 overflow-hidden">
        <table className="w-full text-sm" data-testid="admin-promoters-table">
          <thead className="bg-card/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="text-right p-3 w-10" />
              <th className="text-right p-3">מפיץ</th>
              <th className="text-right p-3">הביא</th>
              <th className="text-right p-3">השבוע</th>
              <th className="text-right p-3">לחיצות</th>
              <th className="text-right p-3">המרה</th>
              <th className="text-right p-3">פעילים</th>
              <th className="text-right p-3">תרומות</th>
              <th className="text-right p-3 w-12" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const isExpanded = expanded === p.user_id;
              return (
                <React.Fragment key={p.user_id}>
                  <tr
                    className="border-t border-border/30 hover:bg-card/40 cursor-pointer"
                    onClick={() => setExpanded(isExpanded ? null : p.user_id)}
                    data-testid={`promoter-row-${p.user_id}`}
                  >
                    <td className="p-3">
                      {isExpanded ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      )}
                    </td>
                    <td className="p-3">
                      <div className="font-heading text-foreground">{p.name || "—"}</div>
                      <div className="text-xs text-muted-foreground" dir="ltr">
                        {p.email}
                      </div>
                    </td>
                    <td className="p-3 font-heading text-base text-foreground">
                      {p.signups_brought}
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {p.signups_brought_this_week > 0 ? (
                        <span className="text-gold">+{p.signups_brought_this_week}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="p-3 text-muted-foreground">{p.clicks_count}</td>
                    <td className="p-3 text-muted-foreground">
                      {p.conversion_rate !== null && p.conversion_rate !== undefined
                        ? `${(p.conversion_rate * 100).toFixed(0)}%`
                        : "—"}
                    </td>
                    <td className="p-3 text-muted-foreground">{p.referred_users_active}</td>
                    <td className="p-3">
                      <span className="font-heading text-foreground">
                        ₪{p.referred_users_donated_total || 0}
                      </span>
                      <span className="text-xs text-muted-foreground mr-1">
                        ({p.referred_users_donated_count})
                      </span>
                    </td>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => setQrPromoter(p)}
                        data-testid={`promoter-qr-${p.user_id}`}
                        title="הצג QR"
                        className="h-8 w-8 hover:text-gold"
                      >
                        <QrCode className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-card/20">
                      <td colSpan={9} className="p-3">
                        <ReferralsPanel userId={p.user_id} includeDummy={includeDummy} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {filtered.map((p) => {
          const isExpanded = expanded === p.user_id;
          return (
            <div
              key={p.user_id}
              className="rounded-md border border-border/60 bg-card/40 p-4"
              data-testid={`promoter-card-${p.user_id}`}
            >
              <button
                onClick={() => setExpanded(isExpanded ? null : p.user_id)}
                className="w-full text-right"
              >
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-foreground truncate">{p.name || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate" dir="ltr">
                      {p.email}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading text-lg text-foreground">{p.signups_brought}</div>
                    <div className="text-[10px] text-muted-foreground">הצטרפויות</div>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs text-muted-foreground">
                  <div>השבוע: {p.signups_brought_this_week || 0}</div>
                  <div>קליקים: {p.clicks_count}</div>
                  <div>תרומות: ₪{p.referred_users_donated_total || 0}</div>
                </div>
              </button>
              <div className="mt-2 flex justify-end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setQrPromoter(p)}
                  data-testid={`promoter-qr-card-${p.user_id}`}
                  className="text-gold hover:text-gold/80"
                >
                  <QrCode className="h-3.5 w-3.5 ml-1" strokeWidth={1.5} />
                  קוד QR
                </Button>
              </div>
              {isExpanded && (
                <div className="mt-3 border-t border-border/40 pt-3">
                  <ReferralsPanel userId={p.user_id} includeDummy={includeDummy} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div ref={sentinelRef} className="h-8 flex items-center justify-center">
        {loading && <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />}
        {!loading && !hasMore && items.length > 0 && (
          <p className="text-xs text-muted-foreground">— סוף הרשימה —</p>
        )}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-muted-foreground py-6">אין עדיין מפיצים.</p>
        )}
      </div>

      <PromoterQRDialog
        open={!!qrPromoter}
        onOpenChange={(o) => !o && setQrPromoter(null)}
        promoter={qrPromoter}
      />
    </div>
  );
}
